import React, { ReactNode, useEffect, useState } from 'react';
import { Navbar, Button, Breadcrumbs, Drawer, Menu } from 'react-daisyui';
import { useLocation, Link, NavLink, useNavigate } from 'react-router-dom';
import FeedbackDialog from './FeedbackDialog';
import { useAuth } from '../../../modules/authentication/AuthProvider';
import { useTranslation } from 'react-i18next';
import { ListFilter, Search, User } from 'lucide-react';
import routes from '../../../modules/routes/Routes';
import { useProfilePages } from '../ProfilePage';
import ErrorBoundary from './ErrorBoundary';

interface PageLayoutProps {
  children: ReactNode;
  navbarStart?: ReactNode; // allows overriding the default logo area
  navbarCenter?: ReactNode;
  navbarEnd?: ReactNode;
  drawerMenuItems?: ReactNode;
  currentPage: string;
  ignorePageMargin?: boolean;
}

const PageLayout: React.FC<PageLayoutProps> = ({ children, navbarStart, navbarCenter, navbarEnd, drawerMenuItems, currentPage, ignorePageMargin }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const { logout } = useAuth();
    const { t } = useTranslation();

    return (<>
        <div
            id="navbar-wrapper"
            className="container fixed inset-x-0 top-0 backdrop-blur-sm transition-all duration-500 border xl:mt-4 mt-0 xl:rounded-full z-20 bg-base-100 lg:bg-opacity-95 border-base-content/10"
        >
                <Navbar className="gap-3 px-0">
                    <Navbar.Start className="w-fit gap-2 lg:w-1/3">
                        <div className="flex-none md:hidden">
                            <Button color="ghost" onClick={() => setIsDrawerOpen(true)}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block w-5 h-5 stroke-current">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                                </svg>
                            </Button>
                        </div>
                        {navbarStart ? (
                            <div className="flex items-center">
                                {navbarStart}
                            </div>
                        ) : (
                            <Link to="/" className="flex items-center" data-umami-event="Matching Open">
                                <img src="/app/LogoHorizontal.svg" alt="FindMeinZuhause.de" className="h-12 w-auto" />
                            </Link>
                        )}
                    </Navbar.Start>

                    {navbarCenter && 
                        <Navbar.Center className="hidden md:block">
                            {navbarCenter}
                        </Navbar.Center>
                    }

                    {navbarEnd && 
                        <Navbar.End className="gap-3 text-end hidden md:block w-full">
                            {navbarEnd}
                        </Navbar.End>
                    }
                </Navbar>

                {/* sm screen menu (drawer) */}
                <Drawer open={isDrawerOpen} onClickOverlay={() => setIsDrawerOpen(false)} side={
                    <Menu className="min-h-full w-80 gap-2 bg-base-100 p-4 text-base-content">
                        <Menu.Item className="font-medium">
                            <Link to="/" className="flex items-center" data-umami-event="Matching Open">
                                <img src="/app/logo.svg" alt="FindMeinZuhause.de" className="h-12 w-auto" />
                            </Link>
                        </Menu.Item>

                        {drawerMenuItems}

                        <Menu.Item className="font-medium">
                            <FeedbackDialog currentPage={currentPage} />
                        </Menu.Item>
                        <Menu.Item className="font-medium">
                            <Button color="ghost" size="sm" data-umami-event="Logout" onClick={logout}>
                                {t('components.pageLayout.logout')}
                            </Button>
                        </Menu.Item>
                    </Menu>
                } />
            </div>
        <div className="bg-gray-50 absolute w-full h-full overflow-y-auto">
        <div className={`container mx-auto px-4 relative ${ignorePageMargin ? '' : 'mt-24'}`}>
            <ErrorBoundary>{children}</ErrorBoundary>
        </div>
        </div>
    </>);
};

export default PageLayout;

export const AppPageLayout: React.FC<React.PropsWithChildren<{ currentPage: string; navbarStart?: ReactNode }>> = ({ children, currentPage, navbarStart }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const profilePages = useProfilePages();

    const isActive = (page: string) => currentPage === page ? 'btn-active' : '';

    return (
        <PageLayout
            currentPage={currentPage}
            navbarStart={navbarStart}
            navbarEnd={
                <>
                    <ul className="menu menu-horizontal menu-sm gap-1 2xl:gap-2">
                        <li className="font-medium">
                            <Button
                                color="ghost"
                                size="sm"
                                onClick={() => navigate(routes.app.profile.searchPreferences.build())}
                                className={isActive('SearchPreferences')}
                            >
                                {t('components.appPageLayout.menu.searchPreferences')}
                            </Button>
                        </li>
                        <div className="divider divider-horizontal mx-1"></div>
                        <li className="font-medium">
                            <Button
                                color="ghost"
                                size="sm"
                                onClick={() => navigate(routes.app.profile.telegramRegistration.build())}
                                className={isActive('TelegramRegistration')}
                            >
                                {t('components.appPageLayout.menu.settings')}
                            </Button>
                        </li>
                        <li className="font-medium">
                            <Button
                                color="primary"
                                size="sm"
                                onClick={() => navigate(routes.app.matches.build())}
                                className={isActive('matches')}
                            >
                                {t('components.appPageLayout.menu.matches')}
                            </Button>
                        </li>
                    </ul>
                </>
            }
            drawerMenuItems={
                <>
                    <Menu.Item 
                        key="matches"
                        className={`font-medium ${currentPage === 'matches' ? 'text-primary' : ''}`}
                        onClick={() => navigate(routes.app.matches.build())}
                    >
                        <div className="flex items-center space-x-2">
                            <ListFilter size={18} />
                            {t('components.appPageLayout.menu.matches')}
                        </div>
                    </Menu.Item>
                    <div className="divider my-2"></div>
                    {profilePages.map(page => (
                        page.key.includes('divider') ? (
                            <div key={page.key} className="divider my-2"></div>
                        ) : (
                            <Menu.Item 
                                key={page.key} 
                                className={`font-medium ${currentPage === page.key ? 'text-primary' : ''}`}
                                onClick={() => navigate(Object.values(routes.app.profile).filter(p => p.key === page.key)[0].build())}
                            >
                                <div className="flex items-center space-x-2">
                                    {(page as typeof profilePages[0]).icon}
                                    {page.name}
                                </div>
                            </Menu.Item>
                        )
                    ))}
                </>
            }
        >
            {children}
        </PageLayout>
    );
};
